import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import { loadSheetBitmap } from './fieldwireTiles';
import SheetZoomPane from './SheetZoomPane';
import useSharpSheet from './useSharpSheet';

// ── ✋ Help task — tasking a plan set by hand, fast (Ryan, Oct 21 2026) ───────────
// Clip a table off a sheet (the Pull Box Schedule, a pole schedule, a legend); the
// reader turns it into rows you check; the clip and its rows stay on the left while you
// flip through the sheets on the right. Find 01a on the plan → drag its row there (or
// tap the row, then tap the spot) → the Fieldwire task is made at once, named
// "01a · No. 9 Pull Box" with the row's other columns as its first comment. Dropping a
// row that is already on a sheet moves its task. Undo takes a task this window made back
// off. A loose task (any category, any name) can be dropped the same way.

const NAVY = '#1e3a5f', PURPLE = '#7c3aed', GREEN = '#15803d';
const btn = { padding: '6px 11px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const sel = { border: '1px solid #e5e7eb', borderRadius: 6, padding: '3px 6px', fontSize: 12, background: '#fff', maxWidth: 150 };
const inp = { border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 8px', fontSize: 12.5, fontFamily: 'inherit' };
const lbl = { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };

// "NO. 7 PULL BOX, MOD." → "No. 7 Pull Box, Mod."
// A type column that is only a number ("35", "35a" on a pole schedule) reads "Type 35" (Ryan, Oct 22 2026).
const nice = s => { const t = String(s || '').trim(); return /^\d+[a-z]?$/i.test(t) ? `Type ${t.toUpperCase()}` : t.toLowerCase().replace(/(^|[\s(/-])([a-z])/g, (m, a, b) => a + b.toUpperCase()); };
export const rowTaskName = r => [String(r.id || '').trim(), nice(r.type)].filter(Boolean).join(' · ') || 'Task';
const rowNote = (clip, r) => [`${clip.name || 'Table'}${r.id ? ' · ' + r.id : ''}${r.type ? ' — ' + r.type : ''}`, ...String(r.details || '').split(/;\s*/).filter(Boolean)].join('\n');

// The clip picture comes through the API with the session token, so it's fetched as a blob.
function useClipImage(projectId, clipId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!clipId) { setUrl(null); return undefined; }
    let gone = false, made = null;
    fetch(`${API_BASE}/projects/${projectId}/fieldwire/clips/${clipId}/image`, { headers: { 'x-worker-token': getToken() } })
      .then(r => (r.ok ? r.blob() : null)).then(b => { if (b && !gone) { made = URL.createObjectURL(b); setUrl(made); } }).catch(() => {});
    return () => { gone = true; if (made) URL.revokeObjectURL(made); };
  }, [projectId, clipId]);
  return url;
}

// Cut the rectangle (fractions of the sheet) out of the sharpest picture we have.
async function cutClip(projectId, sheet, src, rect) {
  let source;
  if (src && src.startsWith('blob:')) {
    source = await new Promise((res, rej) => { const im = new Image(); im.onload = () => res(im); im.onerror = () => rej(new Error('The sheet picture would not load.')); im.src = src; });
  } else source = await loadSheetBitmap(projectId, sheet.id);
  const W = source.naturalWidth || source.width, H = source.naturalHeight || source.height;
  const sx = Math.round(rect.x * W), sy = Math.round(rect.y * H), sw = Math.max(4, Math.round(rect.w * W)), sh = Math.max(4, Math.round(rect.h * H));
  const k = Math.min(1, 3200 / Math.max(sw, sh));
  const c = document.createElement('canvas'); c.width = Math.round(sw * k); c.height = Math.round(sh * k);
  const g = c.getContext('2d'); g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);
  g.drawImage(source, sx, sy, sw, sh, 0, 0, c.width, c.height);
  const blob = await new Promise(res => c.toBlob(res, 'image/png'));
  if (!blob) throw new Error('Could not cut the clip.');
  return { blob, width: c.width, height: c.height };
}

export default function HandTaskTool({ projectId, teams: teamsIn = [], onClose, onChanged }) {
  const [teams, setTeams] = useState(teamsIn);
  const [sheets, setSheets] = useState([]);
  const [sheetIdx, setSheetIdx] = useState(0);
  const [tasks, setTasks] = useState([]);
  const [clips, setClips] = useState([]);
  const [clipId, setClipId] = useState(null);
  const [mode, setMode] = useState('drop');          // drop | clip
  const [rect, setRect] = useState(null);            // the box being drawn (fractions)
  const [clipName, setClipName] = useState('Pull Box Schedule');
  const [armed, setArmed] = useState(null);          // { kind:'row', key } | { kind:'loose' }
  const [loose, setLoose] = useState({ team_id: '', name: '' });
  const [made, setMade] = useState([]);              // loose drops this visit, for Undo
  const [editKey, setEditKey] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [bigClip, setBigClip] = useState(false);
  const frameRef = useRef(null);
  const drawRef = useRef(null);

  const sheet = sheets[sheetIdx] || null;
  const { src, note } = useSharpSheet(projectId, sheet);
  const clip = clips.find(c => c.id === clipId) || null;
  const clipImg = useClipImage(projectId, clipId);
  const rows = clip?.rows || [];
  const placedN = rows.filter(r => r.task_id).length;
  const teamName = id => teams.find(t => t.id === id)?.name || '';
  // ＋ New category… at the bottom of every category list (Ryan, Oct 22 2026: "I need to be
  // able to add a category during this step"). Made in Fieldwire, then picked. From a row
  // it is also set on every other row of the same type still without one.
  async function newCategory(suggest = '') {
    const name = (window.prompt('Name of the new Fieldwire category:', suggest) || '').trim();
    if (!name) return null;
    const have = teams.find(t => t.name.trim().toLowerCase() === name.toLowerCase());
    if (have) return have.id;
    setBusy('cat'); setErr('');
    try {
      const r = await apiFetch(`/projects/${projectId}/fieldwire/teams`, { method: 'POST', body: JSON.stringify({ name }) });
      const list = Array.isArray(r.teams) ? r.teams : teams;
      setTeams(list);
      const made = list.find(t => t.name.trim().toLowerCase() === name.toLowerCase());
      setMsg(made ? `Category “${name}” added in Fieldwire` : `Category “${name}” sent to Fieldwire — it shows in the lists after the next sync (↻ on the pillow).`);
      onChanged && onChanged();
      return made ? made.id : null;
    } catch (e) { setErr(e?.error || 'Could not add the category.'); return null; }
    finally { setBusy(''); }
  }
  async function pickRowTeam(r, value) {
    if (value !== '__new') return setRow(r.key, { team_id: value || null });
    const id = await newCategory(nice(r.type));
    if (!id) return undefined;
    const t = String(r.type || '').trim().toLowerCase();
    return patchClip({ rows: rows.map(z => (z.key === r.key || (t && !z.team_id && !z.task_id && String(z.type || '').trim().toLowerCase() === t) ? { ...z, team_id: id } : z)) });
  }

  const loadTasks = useCallback(() => apiFetch(`/projects/${projectId}/fieldwire/tasks`).then(t => setTasks(Array.isArray(t) ? t : [])).catch(() => {}), [projectId]);
  useEffect(() => {
    apiFetch(`/projects/${projectId}/fieldwire/sheets`).then(s => setSheets(Array.isArray(s) ? s : [])).catch(e => setErr(e?.error || 'Could not load the sheets.'));
    syncNow(true);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Both ways with Fieldwire (Ryan, Oct 22 2026): pull the latest first, then the rows are
  // checked against it on the server — a task deleted in Fieldwire puts its row back to a to-do.
  const [syncing, setSyncing] = useState(false);
  async function syncNow(first = false) {
    setSyncing(true);
    try {
      const c = await apiFetch(`/projects/${projectId}/fieldwire/clips?sync=1`);
      const list = Array.isArray(c) ? c : [];
      setClips(list);
      if (first) { if (list.length) setClipId(list[list.length - 1].id); else setMode('clip'); }
      await loadTasks();
      const lost = list.reduce((n, x) => n + x.rows.filter(r => r.lost_at && !r.task_id).length, 0);
      if (!first || lost) setMsg(lost ? `Synced with Fieldwire — ${lost} row${lost === 1 ? ' was' : 's were'} deleted in Fieldwire and ${lost === 1 ? 'is' : 'are'} back to place again.` : 'Synced with Fieldwire — everything matches.');
    } catch (e) { setErr(e?.error || 'Could not sync with Fieldwire.'); }
    setSyncing(false);
  }
  const putClip = c => setClips(cs => (cs.some(x => x.id === c.id) ? cs.map(x => (x.id === c.id ? c : x)) : [...cs, c]));

  // Keys: Esc lets go of what's armed (then closes), ← → flip sheets when not typing.
  useEffect(() => {
    const k = e => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '');
      if (e.key === 'Escape') { if (armed) setArmed(null); else if (mode === 'clip' && clips.length) { setMode('drop'); setRect(null); } else onClose(); }
      else if (!typing && e.key === 'ArrowRight') setSheetIdx(i => Math.min(sheets.length - 1, i + 1));
      else if (!typing && e.key === 'ArrowLeft') setSheetIdx(i => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [armed, mode, clips.length, sheets.length, onClose]);

  // ── Clipping ──
  const frac = (e, el) => { const r = el.getBoundingClientRect(); return { fx: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), fy: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) }; };
  function clipDown(e) {
    if (mode !== 'clip') return;
    e.stopPropagation(); e.preventDefault();
    const { fx, fy } = frac(e, frameRef.current);
    drawRef.current = { fx, fy };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* old browser */ }
    setRect({ x: fx, y: fy, w: 0, h: 0 });
  }
  function clipMove(e) {
    const d = drawRef.current; if (mode !== 'clip' || !d) return;
    e.stopPropagation();
    const { fx, fy } = frac(e, frameRef.current);
    setRect({ x: Math.min(d.fx, fx), y: Math.min(d.fy, fy), w: Math.abs(fx - d.fx), h: Math.abs(fy - d.fy) });
  }
  function clipUp(e) { if (mode === 'clip' && drawRef.current) { e.stopPropagation(); drawRef.current = null; } }
  async function saveClip() {
    if (!sheet || !rect || rect.w < 0.01 || rect.h < 0.01 || busy) return;
    setBusy('clip'); setErr(''); setMsg('');
    try {
      const { blob, width, height } = await cutClip(projectId, sheet, src, rect);
      const fd = new FormData();
      fd.append('image', blob, 'clip.png');
      fd.append('floorplan_id', sheet.id); fd.append('name', clipName.trim() || 'Clipped table');
      fd.append('rect', JSON.stringify(rect)); fd.append('width', String(width)); fd.append('height', String(height));
      const r = await fetch(`${API_BASE}/projects/${projectId}/fieldwire/clips`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Could not save the clip (${r.status})`);
      putClip(j); setClipId(j.id); setMode('drop'); setRect(null);
      await readClip(j.id);
    } catch (e) { setErr(e?.message || 'Could not save the clip.'); }
    setBusy('');
  }
  async function readClip(id = clipId) {
    if (!id) return;
    setBusy('read'); setErr(''); setMsg('Reading the table…');
    try {
      const r = await apiFetch(`/projects/${projectId}/fieldwire/clips/${id}/read`, { method: 'POST', body: '{}' });
      putClip(r);
      setMsg(r.read && r.read.found ? `Read ${r.read.found} row${r.read.found === 1 ? '' : 's'} — check them against the clip.` : 'The reader found no rows — add them with ＋ Add a row.');
    } catch (e) { setErr(e?.error || 'The reader did not answer.'); setMsg(''); }
    setBusy('');
  }
  async function patchClip(body) {
    if (!clip) return;
    try {
      const r = await apiFetch(`/projects/${projectId}/fieldwire/clips/${clip.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      putClip(r);
      // A placed row's new name / category goes to its Fieldwire task too.
      if (r.fw_failed && r.fw_failed.length) setErr(`Saved here, but Fieldwire did not take: ${r.fw_failed.map(f => f.row).join(', ')}`);
      else if (body.rows && rows.some(x => x.task_id)) loadTasks();
    }
    catch (e) { setErr(e?.error || 'Could not save that.'); }
  }
  const setRow = (key, patch) => patchClip({ rows: rows.map(r => (r.key === key ? { ...r, ...patch } : r)) });
  async function removeClip() {
    if (!clip || !window.confirm(`Take the clip “${clip.name}” away? Tasks already made stay in Fieldwire.`)) return;
    try { await apiFetch(`/projects/${projectId}/fieldwire/clips/${clip.id}`, { method: 'DELETE' }); const rest = clips.filter(c => c.id !== clip.id); setClips(rest); setClipId(rest.length ? rest[rest.length - 1].id : null); if (!rest.length) setMode('clip'); }
    catch (e) { setErr(e?.error || 'Could not remove the clip.'); }
  }

  // ── Dropping ──
  async function dropAt(what, fx, fy) {
    if (!sheet || busy) return;
    const x = Math.round(fx * (sheet.file_width || 1)), y = Math.round(fy * (sheet.file_height || 1));
    let body;
    if (what.kind === 'row') {
      const r = rows.find(z => z.key === what.key); if (!r || !clip) return;
      body = { floorplan_id: sheet.id, x, y, name: rowTaskName(r), team_id: r.team_id || clip.team_id || null, note: rowNote(clip, r), clip_id: clip.id, row_key: r.key };
    } else {
      if (!loose.name.trim()) { setErr('Give the loose task a name first.'); return; }
      body = { floorplan_id: sheet.id, x, y, name: loose.name.trim(), team_id: loose.team_id || null };
    }
    setBusy('drop'); setErr(''); setMsg('');
    try {
      const r = await apiFetch(`/projects/${projectId}/fieldwire/hand-tasks`, { method: 'POST', body: JSON.stringify(body) });
      if (r.clip) putClip(r.clip);
      if (what.kind === 'loose') setMade(m => [{ id: r.task.id, name: body.name, sheet: sheet.name }, ...m].slice(0, 8));
      setMsg(`${r.moved ? 'Moved' : 'Made'} ${r.task.sequence_number != null ? '#' + r.task.sequence_number + ' ' : ''}“${body.name}” on ${sheet.name}`);
      setArmed(null);
      await loadTasks(); onChanged && onChanged();
    } catch (e) { setErr(e?.error || 'Fieldwire did not take it.'); }
    setBusy('');
  }
  async function undo(taskId) {
    if (busy) return;
    setBusy('undo'); setErr('');
    try {
      const r = await apiFetch(`/projects/${projectId}/fieldwire/hand-tasks/${taskId}`, { method: 'DELETE' });
      if (r.clip) putClip(r.clip);
      setMade(m => m.filter(x => x.id !== taskId));
      setMsg('Undone — the task is off Fieldwire.');
      await loadTasks(); onChanged && onChanged();
    } catch (e) { setErr(e?.error || 'Could not undo it.'); }
    setBusy('');
  }
  function onSheetDrop(e) {
    e.preventDefault();
    const key = e.dataTransfer.getData('text/x-row') || '';
    if (!key || !frameRef.current) return;
    const { fx, fy } = frac(e, frameRef.current);
    dropAt(key === '__loose' ? { kind: 'loose' } : { kind: 'row', key }, fx, fy);
  }
  function onSheetClick(e) {
    if (mode !== 'drop' || !armed || !frameRef.current) return;
    const { fx, fy } = frac(e, frameRef.current);
    dropAt(armed, fx, fy);
  }
  function goToRow(r) {
    if (r.floorplan_id) { const i = sheets.findIndex(s => s.id === r.floorplan_id); if (i >= 0) setSheetIdx(i); }
  }

  const onSheet = sheet ? tasks.filter(t => t.floorplan_id === sheet.id && t.pos_x != null && t.pos_y != null) : [];
  const rowTaskIds = new Set(rows.filter(r => r.task_id).map(r => r.task_id));
  const armedRow = armed?.kind === 'row' ? rows.find(r => r.key === armed.key) : null;
  const overlay = scale => (
    <div ref={frameRef} onDragOver={e => { if (mode === 'drop') e.preventDefault(); }} onDrop={onSheetDrop} onClick={onSheetClick}
      onPointerDown={clipDown} onPointerMove={clipMove} onPointerUp={clipUp}
      style={{ position: 'absolute', inset: 0, cursor: mode === 'clip' ? 'crosshair' : armed ? 'copy' : undefined }}>
      {sheet && onSheet.map(t => {
        const mine = rowTaskIds.has(t.id);
        return (
          <div key={t.id} title={`#${t.sequence_number ?? '?'} ${t.name || ''}${t.team_name ? ' · ' + t.team_name : ''}`}
            style={{ position: 'absolute', left: `${(t.pos_x / (sheet.file_width || 1)) * 100}%`, top: `${(t.pos_y / (sheet.file_height || 1)) * 100}%`,
              transform: `translate(-50%, -50%) scale(${1 / scale})`, transformOrigin: 'center', pointerEvents: 'auto' }}>
            <div style={{ minWidth: 16, height: 16, padding: '0 4px', borderRadius: 999, background: mine ? PURPLE : (t.team_color || '#64748b'), color: '#fff', fontSize: 9.5, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', whiteSpace: 'nowrap' }}>
              {mine ? (t.name || '').split(' · ')[0] || t.sequence_number : t.sequence_number}
            </div>
          </div>
        );
      })}
      {mode === 'clip' && rect && (
        <div style={{ position: 'absolute', left: `${rect.x * 100}%`, top: `${rect.y * 100}%`, width: `${rect.w * 100}%`, height: `${rect.h * 100}%`,
          border: `${2 / scale}px dashed ${PURPLE}`, background: 'rgba(124,58,237,0.08)', pointerEvents: 'none' }} />
      )}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 6000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: NAVY }}>✋ Help task</div>
        <div style={{ fontSize: 12.5, color: '#6b7280', flex: 1 }}>Clip a table, then drag each row onto the plan where you find it — each drop is a Fieldwire task right away.</div>
        <button onClick={() => syncNow(false)} disabled={syncing} title="Pull the latest from Fieldwire — tasks deleted or moved there show here" style={{ ...btn, padding: '5px 10px', fontSize: 12 }}>{syncing ? 'Syncing…' : '↻ Sync with Fieldwire'}</button>
        <button onClick={onClose} aria-label="Close" style={{ ...btn, fontSize: 16, padding: '4px 11px' }}>×</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* ── Left: the clip and its rows ── */}
        <div style={{ width: 400, flexShrink: 0, borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
            {clips.map(c => (
              <button key={c.id} onClick={() => { setClipId(c.id); setMode('drop'); setArmed(null); }}
                style={{ ...btn, padding: '4px 10px', fontSize: 12, background: c.id === clipId && mode === 'drop' ? NAVY : '#fff', color: c.id === clipId && mode === 'drop' ? '#fff' : '#374151' }}>
                {c.name} <span style={{ opacity: 0.7 }}>{c.rows.filter(r => r.task_id).length}/{c.rows.length}</span>
              </button>
            ))}
            <button onClick={() => { setMode('clip'); setRect(null); setArmed(null); }} style={{ ...btn, padding: '4px 10px', fontSize: 12, borderStyle: 'dashed', color: PURPLE, borderColor: '#c4b5fd' }}>＋ Clip a table</button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {mode === 'clip' ? (
              <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: PURPLE }}>Clip a table</div>
                <div style={{ fontSize: 12.5, color: '#4b5563' }}>Go to the sheet with the table (‹ › above it), zoom in if it helps, then <b>drag a box around the table</b> — a Pull Box Schedule, a pole schedule, a legend. The reader turns it into rows you can check.</div>
                <input value={clipName} onChange={e => setClipName(e.target.value)} placeholder="Name it, e.g. Pull Box Schedule" style={inp} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={saveClip} disabled={!rect || rect.w < 0.01 || !!busy} style={{ ...btn, border: 'none', background: rect && rect.w >= 0.01 ? PURPLE : '#ddd6fe', color: '#fff' }}>{busy === 'clip' ? 'Saving…' : busy === 'read' ? 'Reading…' : '✂ Save the clip and read it'}</button>
                  {clips.length > 0 && <button onClick={() => { setMode('drop'); setRect(null); }} style={btn}>Cancel</button>}
                </div>
                {!rect && <div style={{ fontSize: 11.5, color: '#7c3aed' }}>No box yet — drag on the sheet.</div>}
              </div>
            ) : clip ? (
              <>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto', maxHeight: bigClip ? 'none' : 230, background: '#fff', cursor: 'zoom-in' }} onClick={() => setBigClip(b => !b)} title={bigClip ? 'Smaller' : 'Bigger'}>
                  {clipImg ? <img src={clipImg} alt={clip.name} style={{ display: 'block', width: bigClip ? 'auto' : '100%', maxWidth: bigClip ? 'none' : '100%' }} /> : <div style={{ padding: 20, fontSize: 12, color: '#9ca3af' }}>Loading the clip…</div>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: placedN === rows.length && rows.length ? GREEN : '#374151' }}>{placedN} of {rows.length} on the plans</span>
                  <span style={{ flex: 1 }} />
                  <button onClick={() => readClip()} disabled={!!busy} title="Have the reader read the clip again (rows already on a sheet stay)" style={{ ...btn, padding: '3px 8px', fontSize: 11 }}>{busy === 'read' ? 'Reading…' : '↻ Read again'}</button>
                  <button onClick={removeClip} title="Take this clip away" style={{ ...btn, padding: '3px 8px', fontSize: 11, color: '#b91c1c' }}>Remove clip</button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4b5563' }}>
                  Category for rows without one:
                  <select value={clip.team_id || ''} onChange={async e => { const v = e.target.value; const id = v === '__new' ? await newCategory() : v; if (v !== '__new' || id) patchClip({ team_id: id || null }); }} style={sel}>
                    <option value="">— none —</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    <option value="__new">＋ New category…</option>
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {rows.map(r => {
                    const on = armed?.kind === 'row' && armed.key === r.key;
                    const editing = editKey === r.key;
                    const sheetName = r.floorplan_id ? (sheets.find(s => s.id === r.floorplan_id)?.name || 'a sheet') : null;
                    return (
                      <div key={r.key} draggable={!editing} onDragStart={e => { e.dataTransfer.setData('text/x-row', r.key); e.dataTransfer.effectAllowed = 'copy'; }}
                        onClick={() => { if (editing) return; setArmed(on ? null : { kind: 'row', key: r.key }); if (r.task_id) goToRow(r); }}
                        style={{ border: `1.5px solid ${on ? PURPLE : r.task_id ? '#bbf7d0' : '#e5e7eb'}`, background: on ? '#f5f3ff' : r.task_id ? '#f0fdf4' : '#fff', borderRadius: 8, padding: '6px 9px', cursor: editing ? 'default' : 'grab' }}>
                        {editing ? (
                          <RowEditor row={r} onSave={p => { setEditKey(null); setRow(r.key, p); }} onCancel={() => setEditKey(null)} />
                        ) : (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 13, color: r.task_id ? GREEN : '#9ca3af' }}>{r.task_id ? '✓' : '○'}</span>
                              <span style={{ fontSize: 13, fontWeight: 800, color: '#111827', flex: 1, minWidth: 0 }}>{rowTaskName(r)}</span>
                              <select value={r.team_id || ''} onClick={e => e.stopPropagation()} onChange={e => pickRowTeam(r, e.target.value)} title="The Fieldwire category"
                                style={{ ...sel, maxWidth: 110, color: r.team_id || clip.team_id ? '#374151' : '#b45309' }}>
                                <option value="">{clip.team_id ? `(${teamName(clip.team_id)})` : '— category —'}</option>
                                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                <option value="__new">＋ New category…</option>
                              </select>
                            </div>
                            {r.details && <div title={r.details} style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.details}</div>}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, fontSize: 11.5 }}>
                              {r.task_id
                                ? <span style={{ color: GREEN, fontWeight: 700 }}>#{r.task_seq ?? '…'} on {sheetName}</span>
                                : r.lost_at && !on
                                  ? <span style={{ color: '#b91c1c', fontWeight: 700 }}>⚠ Deleted in Fieldwire{r.lost_seq != null ? ` (#${r.lost_seq})` : ''} — place it again, or ✕ if it's not needed</span>
                                  : <span style={{ color: on ? PURPLE : '#9ca3af', fontWeight: on ? 700 : 400 }}>{on ? 'Now tap the spot on the sheet' : 'Drag onto the sheet, or tap here then tap the spot'}</span>}
                              <span style={{ flex: 1 }} />
                              <button onClick={e => { e.stopPropagation(); setEditKey(r.key); }} style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}>Edit</button>
                              {r.task_id
                                ? <button onClick={e => { e.stopPropagation(); undo(r.task_id); }} disabled={!!busy} title="Take the task back off Fieldwire" style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}>↶ Undo</button>
                                : <button onClick={e => { e.stopPropagation(); patchClip({ rows: rows.filter(z => z.key !== r.key) }); }} title="Not a real row" style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}>✕</button>}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {!rows.length && busy !== 'read' && <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No rows yet — press ↻ Read again, or add them.</div>}
                  <button onClick={() => { const key = 'n' + Date.now().toString(36); patchClip({ rows: [...rows, { key, id: '', type: '', details: '', team_id: null }] }).then(() => setEditKey(key)); }}
                    style={{ ...btn, padding: '5px 10px', fontSize: 12, borderStyle: 'dashed', alignSelf: 'flex-start' }}>＋ Add a row</button>
                </div>
              </>
            ) : <div style={{ fontSize: 13, color: '#9ca3af' }}>No clips yet.</div>}

            {/* A loose task — any category, any name, same drop. */}
            <div style={{ marginTop: 'auto', borderTop: '1px solid #f1f5f9', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={lbl}>Drop a single task</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <select value={loose.team_id} onChange={async e => { const v = e.target.value; if (v !== '__new') { setLoose(l => ({ ...l, team_id: v, name: l.name || teamName(v) })); return; } const id = await newCategory(); if (id) setLoose(l => ({ ...l, team_id: id })); }} style={{ ...sel, maxWidth: 130 }}>
                  <option value="">Category…</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  <option value="__new">＋ New category…</option>
                </select>
                <input value={loose.name} onChange={e => setLoose(l => ({ ...l, name: e.target.value }))} placeholder="Task name" style={{ ...inp, flex: 1, minWidth: 0 }} />
              </div>
              <div draggable={!!loose.name.trim()} onDragStart={e => { e.dataTransfer.setData('text/x-row', '__loose'); }}
                onClick={() => { if (loose.name.trim()) setArmed(armed?.kind === 'loose' ? null : { kind: 'loose' }); }}
                style={{ ...btn, textAlign: 'center', cursor: loose.name.trim() ? 'grab' : 'default', background: armed?.kind === 'loose' ? '#f5f3ff' : '#fff', borderColor: armed?.kind === 'loose' ? PURPLE : '#e5e7eb', color: loose.name.trim() ? PURPLE : '#9ca3af' }}>
                {armed?.kind === 'loose' ? 'Now tap the spot on the sheet' : '✋ Drag me onto the sheet (or tap, then tap the spot)'}
              </div>
              {made.map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: '#4b5563' }}>
                  <span style={{ color: GREEN }}>✓</span><span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name} · {m.sheet}</span>
                  <button onClick={() => undo(m.id)} disabled={!!busy} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: 0 }}>↶ Undo</button>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ── Right: the sheets ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
            <button onClick={() => setSheetIdx(i => Math.max(0, i - 1))} disabled={sheetIdx <= 0} style={{ ...btn, padding: '4px 12px', fontSize: 15 }} aria-label="Previous sheet">‹</button>
            <select value={sheet?.id || ''} onChange={e => setSheetIdx(Math.max(0, sheets.findIndex(s => s.id === e.target.value)))} style={{ ...sel, maxWidth: 320, fontSize: 13, padding: '5px 8px' }}>
              {sheets.map(s => <option key={s.id} value={s.id}>{s.name}{s.description ? ' — ' + s.description : ''}</option>)}
            </select>
            <button onClick={() => setSheetIdx(i => Math.min(sheets.length - 1, i + 1))} disabled={sheetIdx >= sheets.length - 1} style={{ ...btn, padding: '4px 12px', fontSize: 15 }} aria-label="Next sheet">›</button>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{sheets.length ? `${sheetIdx + 1} of ${sheets.length}` : ''} · {onSheet.length} task{onSheet.length === 1 ? '' : 's'} on this sheet</span>
            <span style={{ flex: 1 }} />
            {armedRow && <span style={{ fontSize: 12.5, fontWeight: 800, color: PURPLE }}>Tap where {rowTaskName(armedRow)} is {armedRow.task_id ? '(moves it)' : ''} · <button onClick={() => setArmed(null)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontWeight: 700 }}>cancel</button></span>}
            {mode === 'clip' && <span style={{ fontSize: 12.5, fontWeight: 800, color: PURPLE }}>✂ Drag a box around the table</span>}
          </div>
          {(err || msg) && (
            <div style={{ padding: '6px 12px', fontSize: 12.5, fontWeight: 700, color: err ? '#b91c1c' : GREEN, background: err ? '#fef2f2' : '#f0fdf4', borderBottom: '1px solid #f1f5f9' }}>{err || msg}</div>
          )}
          <SheetZoomPane src={src} sheetKey={sheet && sheet.id} note={busy === 'drop' ? 'Making the task…' : note} width={sheet?.file_width || 0} height={sheet?.file_height || 0}
            overlay={overlay} empty={sheets.length ? 'Loading the sheet…' : 'No sheets on this Fieldwire project yet.'} style={{ flex: 1, minHeight: 0 }} />
        </div>
      </div>
    </div>
  );
}

function RowEditor({ row, onSave, onCancel }) {
  const [v, setV] = useState({ id: row.id || '', type: row.type || '', details: row.details || '' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }} onClick={e => e.stopPropagation()}>
      <div style={{ display: 'flex', gap: 5 }}>
        <input value={v.id} onChange={e => setV({ ...v, id: e.target.value })} placeholder="ID (01a)" style={{ ...inp, width: 70 }} autoFocus />
        <input value={v.type} onChange={e => setV({ ...v, type: e.target.value })} placeholder="Type (NO. 7 PULL BOX)" style={{ ...inp, flex: 1, minWidth: 0 }} />
      </div>
      <textarea value={v.details} onChange={e => setV({ ...v, details: e.target.value })} placeholder="Station, offset, comments…" rows={2} style={{ ...inp, resize: 'vertical' }} />
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onSave(v)} style={{ ...btn, padding: '4px 10px', fontSize: 12, background: NAVY, color: '#fff', border: 'none' }}>Save</button>
        <button onClick={onCancel} style={{ ...btn, padding: '4px 10px', fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}
